import React from "react";
import cn from "clsx";
import { useTranslation } from "react-i18next";

import ImageWithFallback from "helpers/ImageWithFallback";
import {
  DISPLAYED_WAGER_GAMES,
  formatActivationRuleTitle,
  formatCurrencyAmount,
  formatFreeSpinDetails,
  formatRewardFull,
} from "helpers/playerBonusFormatters";
import { UserAttachedBonus, UserBonusOffer } from "types/playerBonuses";

import styles from "./ListBonusesTab.module.scss";

type BonusAccordionItem = UserAttachedBonus | UserBonusOffer;

interface Props {
  item: BonusAccordionItem;
  showFreeSpins?: boolean;
}

const getBalanceLabel = (balance: string, t: (key: string) => string) => {
  if (balance === "real") {
    return t("realBalanceLabel");
  }

  if (balance === "bonus") {
    return t("bonusBalanceLabel");
  }

  return balance;
};

const BonusAccordion: React.FC<Props> = ({ item, showFreeSpins = false }) => {
  const { t } = useTranslation();

  const activationRules = item.activationRules ?? [];
  const rewards = item.rewards ?? [];
  const wagerBalances = item.wagerBalances ?? [];
  const wagerGames = item.wagerGames ?? [];
  const freeSpinList =
    showFreeSpins && "freeSpinList" in item ? (item.freeSpinList ?? []) : [];

  const hasActivationRules = activationRules.length > 0;
  const hasRewards = rewards.length > 0;
  const hasWagerBalances = wagerBalances.length > 0;
  const hasFreeSpins = freeSpinList.length > 0;
  const hasGames = wagerGames.length > 0;

  if (
    !hasActivationRules &&
    !hasRewards &&
    !hasWagerBalances &&
    !hasFreeSpins &&
    !hasGames
  ) {
    return null;
  }

  const displayedGames = wagerGames.slice(0, DISPLAYED_WAGER_GAMES);
  const moreGamesCount = wagerGames.length - DISPLAYED_WAGER_GAMES;

  return (
    <div className={styles.accordion}>
      {hasActivationRules && (
        <section className={styles.plaque}>
          <h4 className={styles.plaqueTitle}>{t("activationRulesTitle")}</h4>
          <div className={styles.plaqueBody}>
            {activationRules.map((rule, index) => {
              const { currency, config } = rule;
              const minLine =
                config.min !== undefined &&
                config.min !== null &&
                config.min !== ""
                  ? `${t("min")}: ${formatCurrencyAmount(currency, config.min)}`
                  : null;
              const maxLine =
                config.max !== undefined &&
                config.max !== null &&
                config.max !== ""
                  ? `${t("max")}: ${formatCurrencyAmount(currency, config.max)}`
                  : null;
              const hasNumberOfDeposits =
                config.numberOfDeposit !== undefined &&
                config.numberOfDeposit !== null;

              return (
                <div
                  key={`${rule.type}-${rule.currency}-${index}`}
                  className={styles.plaqueItem}
                >
                  <div
                    className={cn(styles.plaqueIcon, styles.plaqueIconGreen)}
                  >
                    💰
                  </div>
                  <div className={styles.plaqueItemContent}>
                    <div className={styles.rulePrimary}>
                      <div className={styles.plaqueItemTitle}>
                        {formatActivationRuleTitle(rule.type, t)}
                      </div>
                      {minLine && (
                        <div className={styles.plaqueItemLine}>{minLine}</div>
                      )}
                      {maxLine && (
                        <div className={styles.plaqueItemLine}>{maxLine}</div>
                      )}
                    </div>
                    {hasNumberOfDeposits && (
                      <div className={styles.ruleSecondary}>
                        <div className={styles.plaqueItemTitle}>
                          {t("numberOfDeposits")}
                        </div>
                        <div className={styles.plaqueItemLine}>
                          {config.numberOfDeposit === 0
                            ? t("any")
                            : config.numberOfDeposit}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {hasRewards && (
        <section className={styles.plaque}>
          <h4 className={styles.plaqueTitle}>{t("rewardsTitle")}</h4>
          <div className={styles.plaqueBody}>
            {rewards.map((reward, index) => {
              const isDepositPercentage = reward.type === "depositPercentage";

              return (
                <div
                  key={`${reward.type}-${reward.currency}-${index}`}
                  className={styles.plaqueItem}
                >
                  <div
                    className={cn(
                      styles.plaqueIcon,
                      reward.type === "loyalty" || reward.type === "coins"
                        ? styles.plaqueIconGold
                        : styles.plaqueIconGreen,
                    )}
                  >
                    {reward.type === "loyalty" || reward.type === "coins"
                      ? "★"
                      : reward.type === "freespins"
                        ? "FS"
                        : "％"}
                  </div>
                  <div className={styles.plaqueItemContent}>
                    <div className={styles.plaqueItemTitle}>
                      {isDepositPercentage
                        ? `${reward.config.value}% ${t("depositBonus")}`
                        : formatRewardFull(reward, t)}
                    </div>
                    {isDepositPercentage && reward.config.max !== undefined && (
                      <div className={styles.plaqueItemLine}>
                        {t("upTo")}{" "}
                        {formatCurrencyAmount(
                          reward.currency,
                          reward.config.max,
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {hasWagerBalances && (
        <section className={styles.plaque}>
          <h4 className={styles.plaqueTitle}>{t("wagerBalancesTitle")}</h4>
          <div className={styles.balanceBadges}>
            {wagerBalances.map((balance) => (
              <span
                key={balance}
                className={cn(
                  styles.balanceBadge,
                  balance === "real" && styles.balanceBadgeReal,
                  balance === "bonus" && styles.balanceBadgeBonus,
                )}
              >
                {getBalanceLabel(balance, t)}
              </span>
            ))}
          </div>
        </section>
      )}

      {hasFreeSpins && (
        <section className={styles.plaque}>
          <h4 className={styles.plaqueTitle}>{t("freeSpins")}</h4>
          <div className={styles.plaqueBody}>
            {freeSpinList.map((spin, index) => {
              const details = formatFreeSpinDetails(spin, t, spin.currency);

              return (
                <div
                  key={spin.publicId || `${spin.gameId}-${index}`}
                  className={styles.plaqueItem}
                >
                  <div className={cn(styles.plaqueIcon, styles.plaqueIconGold)}>
                    FS
                  </div>
                  <div className={styles.plaqueItemContent}>
                    {details.map((detail) => (
                      <div key={detail} className={styles.plaqueItemLine}>
                        {detail}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {hasGames && (
        <section className={cn(styles.plaque, styles.plaqueGames)}>
          <h4 className={styles.plaqueTitle}>{t("gamesTitle")}</h4>
          <div className={styles.gamesGrid}>
            {displayedGames.map((game) => (
              <div key={game.gameId} className={styles.gameTile}>
                <ImageWithFallback
                  src={game.image}
                  alt={game.title}
                  className={styles.gameImage}
                  width={48}
                  height={48}
                />
                <div className={styles.gameInfo}>
                  <span className={styles.gameTitle}>{game.title}</span>
                </div>
              </div>
            ))}
          </div>
          {moreGamesCount > 0 && (
            <div className={styles.gamesMore}>
              + {moreGamesCount} {t("more")}
            </div>
          )}
        </section>
      )}
    </div>
  );
};

export default BonusAccordion;
